import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { admin, api } from '../api/client.js'
import { useAuth } from '../auth.jsx'

const EMOJI = { heart: '❤️', paw: '🐾', fish: '🐟' }
const POLL_MS = 3000

/** Poll the live endpoint: chat messages after the last id + recent reactions.
 *  Mount with key={slug}: a new feeder starts from a clean state. */
function useLive(slug, onReactions) {
  const [messages, setMessages] = useState([])
  const [canPost, setCanPost] = useState(false)
  const last = useRef(null)
  const seen = useRef({})
  const cb = useRef(onReactions)
  useEffect(() => {
    cb.current = onReactions
  })

  useEffect(() => {
    let stop = false
    let timer
    last.current = null
    const tick = async () => {
      try {
        const r = await api.live(slug, last.current ?? undefined)
        if (stop) return
        if (r.messages.length) {
          last.current = r.messages[r.messages.length - 1].id
          setMessages((m) => [...m, ...r.messages].slice(-150))
        }
        setCanPost(r.can_post)
        // Counts are "in the last 10 s": float the increase since the previous poll.
        const fresh = {}
        for (const [k, n] of Object.entries(r.reactions)) fresh[k] = Math.max(0, n - (seen.current[k] ?? 0))
        seen.current = r.reactions
        cb.current(fresh)
      } catch {
        // offline for a moment: try again next tick
      }
      if (!stop) timer = setTimeout(tick, document.hidden ? POLL_MS * 4 : POLL_MS)
    }
    tick()
    return () => {
      stop = true
      clearTimeout(timer)
    }
  }, [slug])

  return { messages, setMessages, canPost }
}

/** Hearts / paws / fish flying up over the video. */
export function useFloaters() {
  const [items, setItems] = useState([])
  const add = (kind, n = 1) => {
    const now = Date.now()
    const fresh = Array.from({ length: Math.min(n, 8) }, (_, i) => ({
      id: `${now}-${kind}-${i}-${Math.random()}`,
      kind,
      left: 8 + Math.random() * 30,
      delay: i * 120,
    }))
    setItems((s) => [...s, ...fresh].slice(-40))
    setTimeout(() => setItems((s) => s.filter((x) => !fresh.includes(x))), 3200)
  }
  const layer = (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <style>{'@keyframes up-float{0%{transform:translateY(0) scale(.6);opacity:0}15%{opacity:1}100%{transform:translateY(-260px) scale(1.2);opacity:0}}'}</style>
      {items.map((f) => (
        <span
          key={f.id}
          className="absolute bottom-6 text-3xl"
          style={{ right: `${f.left}%`, animation: `up-float 2.8s ease-out ${f.delay}ms forwards`, opacity: 0 }}
        >
          {EMOJI[f.kind]}
        </span>
      ))}
    </div>
  )
  return { add, layer }
}

export function ReactionButtons({ slug, onReact }) {
  const { t } = useTranslation()
  const send = async (kind) => {
    onReact(kind)
    await api.react(slug, kind).catch(() => {})
  }
  return (
    <div className="flex gap-2" role="group" aria-label={t('live.react')}>
      {Object.entries(EMOJI).map(([kind, e]) => (
        <button
          key={kind}
          type="button"
          onClick={() => send(kind)}
          aria-label={t(`live.reaction.${kind}`)}
          className="flex size-11 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-edge bg-surface text-xl transition hover:scale-110 hover:border-ink active:scale-95"
        >
          {e}
        </button>
      ))}
    </div>
  )
}

export default function LiveChat({ slug, onReactions }) {
  const { t } = useTranslation()
  const { user, isAdmin } = useAuth()
  const { messages, setMessages, canPost } = useLive(slug, onReactions)
  const [text, setText] = useState('')
  const [error, setError] = useState(null)
  const list = useRef(null)
  const atBottom = useRef(true)

  useEffect(() => {
    const el = list.current
    if (el && atBottom.current) el.scrollTop = el.scrollHeight
  }, [messages])

  const send = async (e) => {
    e.preventDefault()
    if (!text.trim()) return
    setError(null)
    try {
      const m = await api.chat(slug, text)
      setText('')
      setMessages((s) => (s.some((x) => x.id === m.id) ? s : [...s, m]))
    } catch (err) {
      setError(err.status === 429 ? t('live.chatTooFast') : err.status === 422 ? t('live.chatNoLinks') : err.message)
    }
  }
  const moderate = async (fn, id) => {
    await fn(id).catch(() => {})
    setMessages((s) => s.map((m) => (m.id === id ? { ...m, hidden: true } : m)))
  }

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-line p-5">
      <h2 className="text-xl font-black">{t('live.chat')}</h2>
      <div
        ref={list}
        onScroll={(e) => {
          const el = e.currentTarget
          atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
        }}
        className="flex max-h-80 min-h-32 flex-col gap-2 overflow-y-auto pr-1"
        aria-live="polite"
      >
        {messages.length === 0 ? <p className="m-0 text-sm text-subtle">{t('live.chatEmpty')}</p> : null}
        {messages.map((m) => (
          <div key={m.id} className={`group flex items-start gap-2 text-[15px] ${m.hidden ? 'opacity-40' : ''}`}>
            <span className="min-w-0 flex-1">
              <b className={m.is_admin ? 'text-teal' : ''}>
                {m.name ?? t('leaderboard.anonymous')}
                {m.rank ? ` ${m.rank.emoji}` : ''}
              </b>{' '}
              <span className="break-words">{m.text}</span>
            </span>
            {user && !m.hidden ? (
              <button
                type="button"
                title={t('live.report')}
                onClick={() => api.report(m.id).then(() => setMessages((s) => s.map((x) => (x.id === m.id ? { ...x, reported: true } : x))))}
                className="cursor-pointer text-xs text-faint opacity-0 group-hover:opacity-100 focus:opacity-100"
              >
                {m.reported ? '✓' : '⚑'}
              </button>
            ) : null}
            {isAdmin && !m.hidden ? (
              <>
                <button type="button" onClick={() => moderate(admin.hideMessage, m.id)} className="cursor-pointer text-xs font-bold text-live">
                  {t('live.hide')}
                </button>
                <button type="button" onClick={() => moderate(admin.banAuthor, m.id)} className="cursor-pointer text-xs font-bold text-live">
                  {t('live.ban')}
                </button>
              </>
            ) : null}
          </div>
        ))}
      </div>
      {user ? (
        canPost ? (
          <form onSubmit={send} className="flex gap-2">
            <input
              value={text}
              maxLength={300}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('live.chatPlaceholder')}
              className="h-11 flex-1 rounded-xl border-[1.5px] border-edge bg-surface px-3 font-medium text-ink outline-none focus:border-teal"
            />
            <button type="submit" className="cursor-pointer rounded-xl bg-teal px-4 font-extrabold text-on-teal">
              {t('live.send')}
            </button>
          </form>
        ) : (
          <p className="m-0 text-sm text-subtle">{t('live.chatBanned')}</p>
        )
      ) : (
        <Link to="/login" state={{ from: `/feeders/${slug}` }} className="text-sm font-extrabold text-teal no-underline">
          {t('live.chatLogin')}
        </Link>
      )}
      {error ? <p role="alert" className="m-0 text-sm font-bold text-live">{error}</p> : null}
    </section>
  )
}
